import { useMutation, useQuery } from "@apollo/client"
import cn from "classnames"
import React, { useState, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { graphql } from "@/api"
import { type Image, type ImageArchiveQuery } from "@/api/graphql"
import { registerImageSearch } from "@/checkout/common"
import LoadingAnimation from "@/common/components/loading"
import Scrollable from "@/common/components/scrollable"
import { isTablet, isMobile } from "@/common/util"
import type Sign from "@/signomatic/app/sign"
import AsyncImage from "@/signomatic/components/async_image"
import SearchBox from "@/signomatic/components/search_box"
import { useSign } from "@/signomatic/contexts"
import { arrayUniq, defaultImageSize } from "@/signomatic/util"
import { ArrowRight, ErrorOutline, ChevronLeftLarge } from "@/skylticons"

const imagesQuery = graphql(`
  query ImageArchive($color: Boolean!) {
    imageTags {
      id
      name

      images(color: $color) {
        id
        color
        width
        height
        description
        usedCount
      }
    }

    imageKeywords {
      id
      name

      images(color: $color) {
        id
        color
        width
        height
        description
        usedCount
      }
    }
  }
`)

const countUpdateDoc = graphql(`
  mutation ImageCountUpdate($id: Int!) {
    imageCountUpdate(id: $id) {
      success
      error
    }
  }
`)

type Section = "all-categories" | "category-page"
type ImageTag = ImageArchiveQuery["imageTags"][0]

const imageSize = ({ width, height }: Pick<Image, "width" | "height">) => {
  let w = 0
  let h = 0

  if (width > height) {
    w = 180
    h = height / (width / w)
  } else {
    h = 180
    w = width / (height / h)
  }

  return { w: Math.round(w), h: Math.round(h) }
}

export const imageURL = (image: Pick<Image, "id" | "width" | "height">, color: boolean) => {
  const { w, h } = imageSize(image)
  return `/image/get/${image.id}?width=${w}&height=${h}&background_color=fff&text_color=000&color=${color}`
}

export const rowImagesCount = isTablet() && !isMobile() ? 15 : 10

export const addImage = (
  sign: Sign,
  image: Pick<Image, "id" | "width" | "height">,
  color: boolean,
  handleAdd: (id?: string) => void,
  name?: string,
  maxHeight?: number,
  maxWidth?: number
) => {
  const ratio = image.height / image.width

  const size = defaultImageSize(sign.state.heightPx, sign.state.widthPx, ratio)

  const objState: Partial<SignImageState> = {
    id: parseInt(image.id, 10),
    colored: color,
    width: size.width,
    height: size.height,
    name,
    maxHeight,
    maxWidth,
  }

  sign.createObject("image", objState)
  handleAdd(image.id)
}

const TopBar: React.FC<{
  category?: string
  section?: Section
  setSection?: (section: Section) => void
  handleBack: () => void
}> = ({ section, category, setSection, handleBack }) => {
  const { t } = useTranslation()

  let header = t("toolbox.add_new_image")

  if (section) {
    switch (section) {
      case "category-page":
        header = category || t("toolbox.most_popular")
        break

      default:
        header = t("toolbox.image_archive")
        break
    }
  }

  return (
    <div className="ml-3 flex h-12 items-center">
      <button
        className="toolbox-back button button-subtle icon-left mb-0 px-2"
        onClick={() => {
          if (setSection && section === "category-page") {
            setSection("all-categories")
          } else {
            handleBack()
          }
        }}
        type="button"
      >
        <ChevronLeftLarge className="!mr-1 h-6 w-6" />
        <span className="text-sm font-semibold">{t("toolbox.back")}</span>
      </button>
      <span className="absolute left-1/2 -translate-x-1/2 text-base font-semibold">{header}</span>
    </div>
  )
}

const CategorySection: React.FC<{
  classes?: string
  handleAdd: (id?: string) => void
  category?: string
  images: ImageTag["images"]
}> = ({ classes, category, images, handleAdd }) => {
  const { t } = useTranslation()

  return (
    <div className={cn("image-archive h-full lg:pb-12", classes)}>
      <div className="category-page h-full">
        <CategoryPage images={images} name={category || t("toolbox.most_popular")} handleAdd={handleAdd} />
      </div>
    </div>
  )
}

const CategoryPage: React.FC<{
  images: Array<Pick<Image, "id" | "width" | "height" | "color">>
  name: string
  handleAdd: (id: string) => void
}> = ({ images, name, handleAdd }) => {
  const sign = useSign()

  if (!sign) {
    return null
  }

  return (
    <Scrollable key={images.length}>
      <div className="category-images grid grid-cols-4 gap-2">
        {images.map(img => (
          <div
            key={img.id}
            className="img-item-wrapper"
            onClick={() => addImage(sign, img, img.color, handleAdd, name)}
          >
            <div
              className="data-hj-suppress w-full bg-contain bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${imageURL(img, img.color)}` }}
            ></div>
          </div>
        ))}
      </div>
    </Scrollable>
  )
}

const CategoryRow: React.FC<{
  images: ImageTag["images"]
  category?: ImageTag
  handleAdd: (id: string) => void
  setSection: (section: Section, tag?: ImageTag) => void
}> = ({ images, category, handleAdd, setSection }) => {
  const { t } = useTranslation()
  const sign = useSign()

  if (!sign) {
    return null
  }

  const name = category?.name || t("toolbox.most_popular")

  return (
    <div className="category-row pb-2 pt-3">
      <div className="header mx-4 flex items-center justify-between">
        <p className="paragraph-sm bold mb-2">{name}</p>
        <a onClick={() => setSection("category-page", category)} className="flex">
          <span className="whitespace-nowrap">{t("toolbox.show_more")}</span>
          <ArrowRight width={24} height={24} />
        </a>
      </div>
      <Scrollable horizontal>
        <div className="image-list ml-4 flex content-start items-start">
          {images.map(img => (
            <div
              key={`${name}-${img.id}`}
              className="img-item-wrapper"
              onClick={() => addImage(sign, img, img.color, handleAdd, name)}
            >
              <AsyncImage
                src={imageURL(img, img.color)}
                loadingElement={() => <LoadingAnimation className="m-auto" />}
              />
            </div>
          ))}
        </div>
      </Scrollable>
    </div>
  )
}

const SearchResults: React.FC<{
  results: ImageTag[]
  handleAdd: (id: string) => void
}> = ({ results, handleAdd }) => {
  const sign = useSign()

  if (!sign) {
    return null
  }

  const shown: string[] = []

  return (
    <div className="category-images grid grid-cols-4 gap-2">
      {results.map(tag =>
        tag.images.map(img => {
          // only show each image once in search results
          if (shown.includes(img.id)) {
            return null
          }

          shown.push(img.id)

          return (
            <div
              key={img.id}
              className="img-item-wrapper relative"
              onClick={() => addImage(sign, img, img.color, handleAdd, tag.name)}
            >
              <AsyncImage
                src={imageURL(img, img.color)}
                loadingElement={() => <LoadingAnimation className="m-auto" />}
              />
              <div className="signomatic-debug-item absolute inset-x-0 bottom-0 bg-white bg-opacity-40">
                {tag.name}
                <br />
                {img.usedCount}
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}

interface SearchSuggestionProps {
  tag: ImageTag
  onClick: (tag: ImageTag) => void
  search?: string
  active?: boolean
}

const SearchSuggestion: React.FC<SearchSuggestionProps> = ({ tag, onClick, active, search }) => {
  const highlightIndices = useMemo(() => {
    if (!search || search === "") {
      return []
    }

    const searchLength = search.length
    return Array.from(tag.name.matchAll(new RegExp(search, "gi")))
      .map(m => m.index)
      .map(i => {
        if (typeof i === "undefined") {
          return []
        }

        return [...Array(searchLength)].map((_, add) => i + add)
      })
      .flat()
  }, [search, tag.name])

  return (
    <p
      className={cn("paragraph-sm mb-0 cursor-pointer py-2 pl-4", {
        "hover:bg-white-3": !active,
        "bg-primary text-white": active,
      })}
      onClick={() => onClick(tag)}
    >
      {Array.from(tag.name).map((letter, i) => (
        <span
          key={i}
          className={cn({
            "text-primary": !active && highlightIndices.includes(i),
            underline: highlightIndices.includes(i),
          })}
        >
          {letter}
        </span>
      ))}
    </p>
  )
}

interface ImageArchiveProps {
  color: boolean
  handleAddImage: (id: string) => void
  handleBack: () => void
  hint: React.ReactElement
}

const ImageArchive: React.FC<ImageArchiveProps> = ({ color, handleAddImage, handleBack, hint }) => {
  const { t } = useTranslation()
  const [selectedCategory, setSelectedCategory] = useState<ImageTag>()
  const [search, setSearch] = useState("")
  const [section, setSection] = useState<Section>("all-categories")
  const { data, loading, error } = useQuery(imagesQuery, { variables: { color } })
  const [updateImageCount] = useMutation(countUpdateDoc)
  const [searchActive, setSearchActive] = useState(false)
  const [selectedSuggestion, setSelectedSuggestion] = useState<ImageTag>()

  const [imageTags, imageIds] = useMemo(() => {
    // 1. filter out tags with no images
    // 2. sort tags by the sum of the used counts of the images in them (desc)
    // 3. sort the image arrays in each tag by used count (desc)
    const tags =
      data?.imageTags
        .filter(t => t.images.length > 0)
        .sort(
          (a, b) =>
            b.images.reduce((prev, image) => prev + image.usedCount, 0) -
            a.images.reduce((prev, image) => prev + image.usedCount, 0)
        )
        .map(t => {
          // graphql results are frozen so we need to make a copy of both the tag and the images array for sorting
          // sort images by usedcount desc
          return { ...t, images: [...t.images].sort((a, b) => b.usedCount - a.usedCount) }
        }) || []

    const ids = arrayUniq(tags.map(t => t.images.map(im => im.id)).flat())

    return [tags, ids]
  }, [data?.imageTags])

  const imageKeywords = useMemo(
    () =>
      data?.imageKeywords
        .filter(t => t.images.find(i => imageIds.includes(i.id)))
        .map(t => {
          // sort images by usedcount desc
          return { ...t, images: [...t.images].sort((a, b) => b.usedCount - a.usedCount) }
        }) || [],
    [data?.imageKeywords, imageIds]
  )

  const searchResults = useMemo(() => {
    const startsWith: ImageTag[] = []
    const wordStartsWith: ImageTag[] = []
    const includes: ImageTag[] = []

    for (const tag of imageTags) {
      const name = tag.name.toLowerCase()

      if (name.startsWith(search)) {
        startsWith.push(tag)
      } else if (name.includes(` ${search}`)) {
        wordStartsWith.push(tag)
      } else if (name.includes(search)) {
        includes.push(tag)
      }
    }

    for (const tag of imageKeywords) {
      const name = tag.name.toLowerCase()

      if (name.startsWith(search)) {
        startsWith.push(tag)
      } else if (name.includes(` ${search}`)) {
        wordStartsWith.push(tag)
      } else if (name.includes(search)) {
        includes.push(tag)
      }
    }

    return [...startsWith, ...wordStartsWith, ...includes]
  }, [search, imageTags, imageKeywords])

  const mostPopular = useMemo(() => {
    return imageTags
      .map(tag => tag.images)
      .flat()
      .filter((value, index, array) => array.indexOf(value) == index) // unique
      .sort((a, b) => b.usedCount - a.usedCount)
      .slice(0, 30)
  }, [imageTags])

  const classes = hint.props.show ? "" : "no-hint"

  const handleAdd = useCallback(
    (id: string) => {
      handleAddImage(id)
      updateImageCount({ variables: { id: parseInt(id, 0) } })
    },
    [updateImageCount, handleAddImage]
  )

  const changeSearchQuery = useCallback(
    (e: string) => {
      setSearch(e.toLowerCase())
      setSearchActive(false)
      setSelectedSuggestion(undefined)
    },
    [setSearch]
  )

  const handleSearch = useCallback(() => {
    registerImageSearch(search)

    if (search.length === 0) {
      return
    }

    setSearchActive(true)
  }, [search])

  const changeSection = useCallback((section: Section, currentTag?: ImageTag) => {
    setSection(section)
    setSelectedCategory(currentTag)

    if (section == "category-page") {
      setSearch("")
    }
  }, [])

  const showSuggestions = search.length > 1 && searchResults.length > 0 && !searchActive

  const searchSuggestionClicked = useCallback(
    (tag: ImageTag) => {
      registerImageSearch(tag.name)
      changeSearchQuery(tag.name)
      handleSearch()
    },
    [changeSearchQuery, handleSearch]
  )

  const handleSearchKeyUp = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        changeSearchQuery("")
        return
      }

      if (e.key === "Enter") {
        selectedSuggestion ? searchSuggestionClicked(selectedSuggestion) : handleSearch()
        return
      }

      if (e.key === "ArrowDown" && showSuggestions) {
        e.preventDefault()

        const next = selectedSuggestion
          ? searchResults[searchResults.indexOf(selectedSuggestion) + 1] || searchResults[0]
          : searchResults[0]
        setSelectedSuggestion(next)
        return
      }

      if (e.key === "ArrowUp" && showSuggestions) {
        e.preventDefault()

        const previous = selectedSuggestion
          ? searchResults[searchResults.indexOf(selectedSuggestion) - 1] || searchResults[searchResults.length - 1]
          : searchResults[searchResults.length - 1]
        setSelectedSuggestion(previous)
        return
      }
    },
    [handleSearch, changeSearchQuery, showSuggestions, selectedSuggestion, searchResults, searchSuggestionClicked]
  )

  if (loading) {
    return (
      <>
        <TopBar handleBack={handleBack} />
        <div className={cn("image-archive", classes)}>
          <div className="search-bar">
            <SearchBox
              placeholder={t("toolbox.search_images")}
              search={search}
              setSearchQuery={changeSearchQuery}
              handleSearch={handleSearch}
              handleKeyUp={handleSearchKeyUp}
            />
          </div>
          <LoadingAnimation className="m-auto" />
        </div>
      </>
    )
  }

  if (error) {
    return <>{`Error! ${error.message}`}</>
  }

  return (
    <>
      <TopBar category={selectedCategory?.name} section={section} setSection={changeSection} handleBack={handleBack} />
      {section === "all-categories" && (
        <div className={cn("image-archive h-full lg:pb-12", classes)}>
          <Scrollable className="popular-categories">
            <div className="search-bar">
              <SearchBox
                placeholder={t("toolbox.search_images")}
                search={search}
                setSearchQuery={changeSearchQuery}
                handleSearch={handleSearch}
                handleKeyUp={handleSearchKeyUp}
              />
              {showSuggestions && (
                <div className="search-results absolute z-10 mt-1 rounded-lg border-1 border-white-20 bg-white shadow-lg">
                  {searchResults.slice(0, 5).map(tag => (
                    <SearchSuggestion
                      key={tag.id}
                      tag={tag}
                      onClick={searchSuggestionClicked}
                      search={search}
                      active={selectedSuggestion === tag}
                    />
                  ))}
                </div>
              )}
            </div>

            {hint}

            {searchActive ? (
              searchResults.length === 0 ? (
                <span className="no-results mt-6 flex flex-col items-center">
                  <span className="flex h-10 w-10 content-center items-center border-1 border-white-20 bg-error-bg">
                    <ErrorOutline />
                  </span>
                  <h4 className="center">{t("help_center.no_results")}</h4>
                  <p className="paragraph-xs center">{t("toolbox.broader_search")}</p>
                </span>
              ) : (
                <SearchResults results={searchResults} handleAdd={handleAdd} />
              )
            ) : (
              <div className="mt-4">
                {mostPopular && (
                  <CategoryRow
                    images={mostPopular.slice(0, rowImagesCount)}
                    handleAdd={handleAdd}
                    setSection={changeSection}
                  />
                )}
                {imageTags.map(tag => (
                  <CategoryRow
                    images={tag.images}
                    category={tag}
                    handleAdd={handleAdd}
                    setSection={changeSection}
                    key={tag.name}
                  />
                ))}
              </div>
            )}
          </Scrollable>
        </div>
      )}

      {section === "category-page" && (
        <CategorySection
          classes={classes}
          handleAdd={handleAdd}
          category={selectedCategory?.name}
          images={selectedCategory?.images || mostPopular}
        />
      )}
    </>
  )
}

export default ImageArchive
